import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@api";
import "./Directory.css";
import DirectoryResults from "./DirectoryResults";
import { US_STATES } from "../../../../utils/usStates.js";
import { useAuth } from "../../../../context/AuthContext";
// import DirectoryAuthMenu from "../../Auth/DirectoryAuthMenu.jsx"

import basketballImg from "../assets/images/basketball.png";
import soccerImg from "../assets/images/soccer.png";
import volleyballImg from "../assets/images/volleyball.png";
import baseballImg from "../assets/images/baseball.png";
import softballImg from "../assets/images/softball.png";
import pickleballImg from "../assets/images/pickleball.png";
import flag_footballImg from "../assets/images/flag_football.png";
import kickballImg from "../assets/images/kickball.png";
import tennisImg from "../assets/images/tennis.png";
import dodgeballImg from "../assets/images/dodgeball.png";
import archeryImg from "../assets/images/archery.png";
import bowlingImg from "../assets/images/bowling.png";
import DirectoryFooter from "../../Components/Footer/DirectoryFooter"
import DirectoryNavbar from "../../Components/navbar/DirectoryNavbar.jsx"

const SPORT_ORDER = [
  "archery",
  "baseball",
  "basketball",
  "bowling",
  "dodgeball",
  "flag_football",
  "kickball",
  "pickleball",
  "soccer",
  "softball",
  "tennis",
  "volleyball",
];

const SPORT_IMAGE_MAP = {
  basketball: basketballImg,
  archery: archeryImg,
  bowling: bowlingImg,
  soccer: soccerImg,
  volleyball: volleyballImg,
  baseball: baseballImg,
  softball: softballImg,
  pickleball: pickleballImg,
  flag_football: flag_footballImg,
  tennis: tennisImg,
  kickball: kickballImg,
  dodgeball: dodgeballImg,
};

function formatSlugLabel(value = "") {
  return value
    .replace("_plus", "+")
    .replace(/_/g, " ")
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function getRoundedListingsLabel(count) {
  if (!count) return "0 listings";
  if (count < 10) return `${count}+ listings`;
  const rounded = Math.floor(count / 10) * 10;
  return `${rounded}+ listings`;
}

function getStateName(stateCode) {
  if (!stateCode) return "";
  const match = US_STATES.find((s) => s.code === stateCode.toUpperCase());
  return match ? match.name : stateCode.toUpperCase();
}

function getSportPath({ state, county, sport }) {
  if (state && county) {
    return `/state/${state.toLowerCase()}/county/${county
      .toLowerCase()
      .replace(/\s+/g, "-")}/sport/${sport}`;
  }
  if (state) {
    return `/state/${state.toLowerCase()}/sport/${sport}`;
  }
  return `/sport/${sport}`;
}

function getAllSportsPath({ state, county }) {
  if (state && county) {
    return `/state/${state.toLowerCase()}/county/${county
      .toLowerCase()
      .replace(/\s+/g, "-")}/sport`;
  }
  if (state) {
    return `/state/${state.toLowerCase()}/sport`;
  }
  return "/sport";
}

function SportIcon({ sport, label, isViewAll = false }) {
  if (isViewAll) {
    return <span className="browse-sport-dots">...</span>;
  }
  const imageSrc = SPORT_IMAGE_MAP[sport];
  if (!imageSrc) return null;
  return (
    <img
      src={imageSrc}
      alt={label}
      className="browse-sport-icon-img"
    />
  );
}


function Directory() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isClaimMode = searchParams.get("claim") === "true";
  const [showClaimGuide, setShowClaimGuide] = useState(isClaimMode);
  const pathSport =
    location.pathname.startsWith("/state/") &&
      location.pathname.split("/")[3] === "county" &&
      location.pathname.split("/")[5] === "sport"
      ? location.pathname.split("/")[6] || ""
      : location.pathname.startsWith("/state/") &&
        location.pathname.split("/")[3] === "sport"
        ? location.pathname.split("/")[4] || ""
        : location.pathname.startsWith("/county/") &&
          location.pathname.split("/")[3] === "sport"
          ? location.pathname.split("/")[4] || ""
          : location.pathname.startsWith("/sport/") &&
            location.pathname.split("/")[2] !== "audience"
            ? location.pathname.split("/")[2] || ""
            : "";

  const pathState = location.pathname.startsWith("/state/")
    ? location.pathname.split("/")[2] || ""
    : "";

  const stateCountySlug =
    location.pathname.startsWith("/state/") &&
      location.pathname.split("/")[3] === "county"
      ? location.pathname.split("/")[4] || ""
      : "";

  const pathCounty =
    location.pathname.startsWith("/county/")
      ? (location.pathname.split("/")[2] || "").replace(/-/g, " ")
      : stateCountySlug &&
        stateCountySlug !== "sport" &&
        stateCountySlug !== "audience"
        ? stateCountySlug.replace(/-/g, " ")
        : "";
  const state = pathState || searchParams.get("state") || "";
  const county = pathCounty || searchParams.get("county") || "";
  const country = (searchParams.get("country") || "US").toUpperCase();

  const selectedSports = pathSport ? [pathSport] : searchParams.getAll("sport");

  const isSportPath =
    location.pathname === "/sport" || location.pathname.startsWith("/sport/");
  const isAudiencePath =
    location.pathname === "/audience" || location.pathname.startsWith("/audience/");
  const isStateSportPath =
    location.pathname.startsWith("/state/") && location.pathname.includes("/sport");
  const isStateCountyAudiencePath =
    location.pathname.startsWith("/state/") &&
    location.pathname.includes("/county/") &&
    location.pathname.includes("/audience");
  const isCountySportPath =
    location.pathname.startsWith("/county/") && location.pathname.includes("/sport");
  const isStateAudiencePath =
    location.pathname.startsWith("/state/") && location.pathname.includes("/audience");
  const isStateCountySportPath =
    location.pathname.startsWith("/state/") &&
    location.pathname.includes("/county/") &&
    location.pathname.includes("/sport");

  const isResultsMode = Boolean(
    isSportPath ||
    isStateSportPath ||
    isStateAudiencePath ||
    isCountySportPath ||
    isStateCountySportPath ||
    isStateCountyAudiencePath ||
    isAudiencePath ||
    searchParams.get("sport") ||
    searchParams.get("gender") ||
    searchParams.get("age") ||
    searchParams.get("type") ||
    searchParams.get("competition_level") ||
    searchParams.get("intent")
  );

  const [browseMetaLoading, setBrowseMetaLoading] = useState(true);
  const [filters, setFilters] = useState({
    sports: [],
    audiences: [],
    program_types: [],
    age_groups: [],
  });
  const [totalListings, setTotalListings] = useState(0);
  const [loadingSport, setLoadingSport] = useState("");

  const handleSportClick = async (sport) => {
    const nextPath = getSportPath({
      state,
      county,
      sport,
    });

    setLoadingSport(sport);

    try {
      const res = await api.get("/api/directory/leagues", {
        params: {
          sport,
          country,
          state: state || undefined,
          county: county || undefined,
          page: 1,
          limit: 10,
        },
      });

      navigate(nextPath, {
        state: {
          preloadedDirectoryResults: {
            leagues: res.data.leagues || [],
            totalPages: res.data.totalPages || 1,
            total: res.data.total || 0,
            sport,
            page: 1,
            limit: 10,
          },
        },
      });
    } catch (err) {
      console.error(`Failed to preload ${sport} results`, err);
      navigate(nextPath);
    } finally {
      setLoadingSport("");
    }
  };



  useEffect(() => {
    const fetchBrowseMeta = async () => {
      setBrowseMetaLoading(true);

      try {
        const endpoint = isResultsMode
          ? "/api/directory/browse-meta"
          : "/api/directory/browse-home";

        const params = isResultsMode
          ? {
            country,
            includeTotalListings: false,
          }
          : {};

        const res = await api.get(endpoint, { params });

        setFilters(
          res.data?.filters || {
            sports: [],
            audiences: [],
            program_types: [],
            age_groups: [],
          }
        );

        setTotalListings(res.data?.totalListings || 0);
      } catch (err) {
        console.error("Failed to fetch directory browse meta", err);

        setFilters({
          sports: [],
          audiences: [],
          program_types: [],
          age_groups: [],
        });

        setTotalListings(0);
      } finally {
        setBrowseMetaLoading(false);
      }
    };

    fetchBrowseMeta();
  }, [country, isResultsMode]);
  
  const browseSports = useMemo(() => {
    const availableSports = (filters.sports || [])
      .map((sport) => sport.toLowerCase())
      .filter((sport) => SPORT_IMAGE_MAP[sport]);

    const ordered = SPORT_ORDER.filter((sport) => availableSports.includes(sport));
    const extras = availableSports.filter((sport) => !ordered.includes(sport));

    return [...ordered, ...extras];
  }, [filters.sports]);

  const locationLabel =
    county && state
      ? `${county}, ${getStateName(state)}`
      : state
        ? getStateName(state)
        : "";

  const title = isResultsMode
    ? `${selectedSports.length ? selectedSports.map(formatSlugLabel).join(", ") : "Sports"} Directory${locationLabel ? ` in ${locationLabel}` : ""
    } | SportingHip`
    : "SportingHip | Browse Sports Directory";

  const desc = isResultsMode
    ? `Browse sports organizations, leagues, clubs, camps, and tournaments${state ? ` in ${getStateName(state)}` : ""
    }${county ? `, ${county}` : ""}.`
    : "Browse sports programs, leagues, and organizations by sport.";

  return (
    <div className="main-container">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={`https://sportinghip.com${window.location.pathname}`} />
      </Helmet>

      {/* <div className="directory-top-bar">
        <div className="directory-top-left">
          <NavLink to="/" className="directory-brand-block">
            <div className="directory-main-logo">
              <span className="directory-logo-sporting">Sporting</span>
              <span className="directory-logo-hip">Hip</span>
            </div>
            <div className="directory-intro">Sports made easy to find</div>
          </NavLink>
        </div>

        <div className="directory-auth-actions">
          <DirectoryAuthMenu />
        </div>
      </div> */}
      <DirectoryNavbar />
      {showClaimGuide && (
        <div className="claim-modal-backdrop">
          <div className="claim-modal">
            <h2>Find your listing</h2>

            <p>
              Search for your listing, then click “Claim this listing.”
            </p>

            <button type="button" onClick={() => setShowClaimGuide(false)}>
              Got it
            </button>
          </div>
        </div>
      )}
      <div className={`directory-container ${isResultsMode ? "results-mode" : "browse-mode"}`}>
        {browseMetaLoading && !isResultsMode ?(
          <div className="directory-page-spinner">
            <div className="directory-page-spinner-circle" />
          </div>
        ) : (
          <>
            {!isResultsMode && (
              <section className="directory-browse-shell">
                <div className="directory-browse-hero">
                  <h1 className="directory-browse-title">Choose a Sport</h1>
                  <p className="directory-browse-subtitle">
                      Explore leagues, programs, and organizations by sport.
                  </p>

                  <div className="directory-browse-count">
                    <span className="directory-browse-count-icon" aria-hidden="true">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 3l7 3v6c0 5-3.5 8-7 9-3.5-1-7-4-7-9V6l7-3z" />
                        <path d="M9 12l2 2 4-4" />
                      </svg>
                    </span>
                    <span>{getRoundedListingsLabel(totalListings)} and growing</span>
                  </div>
                </div>

                <div className="directory-sport-grid">
                  {browseSports.map((sport) => (
                    <button
                      key={sport}
                      type="button"
                      className={`directory-sport-card ${loadingSport === sport ? `sport-card-loading sport-loading-${sport}` : ""
                        }`}
                      disabled={Boolean(loadingSport)}
                      onClick={() => handleSportClick(sport)}
                    >
                      <div className="browse-sport-icon">
                        <SportIcon sport={sport} label={formatSlugLabel(sport)} />
                      </div>
                      <div className="browse-sport-label">{formatSlugLabel(sport)}</div>
                    </button>
                  ))}

                  <button
                    type="button"
                    className="directory-sport-card directory-sport-card-view-all"
                    onClick={() =>
                      navigate(
                        getAllSportsPath({
                          state,
                          county,
                        })
                      )
                    }
                  >
                      <div className="browse-sport-icon">
                        <span className="browse-sport-view-all-arrow" aria-hidden="true">→</span>
                      </div>
                      <div className="browse-sport-label browse-sport-label-link">View All Sports</div>
                  </button>
                </div>


                  {/* <section className="directory-org-demo-section">
                    <div className="directory-org-demo-content">
                      <div className="directory-org-demo-left">
                        <span className="directory-org-demo-eyebrow">For Organizations</span>

                        <h2>League Management Made Simple</h2>

                        <p>
                          Create seasons, leagues, teams, games, schedules, players, and stats in
                          a free demo. See how your public league page updates from the admin side.
                        </p>

                        <NavLink to="/demo" className="directory-org-demo-btn">
                          Try Free Demo
                          <span aria-hidden="true">→</span>
                        </NavLink>
                      </div>

                      <div className="directory-org-demo-preview">
                        <div className="directory-demo-panel directory-demo-admin-panel">
                          <div className="directory-demo-panel-header">Admin View</div>

                          <div className="directory-demo-admin-note">
                            Add teams, games, players, and stats from one simple dashboard.
                          </div>

                          <div className="directory-demo-breadcrumb">
                            <span>Seasons</span> &gt; <span>Season 1</span> &gt;{" "}
                            <strong>Adult Men 18+</strong>
                          </div>

                          <div className="directory-demo-row-between">
                            <span className="directory-demo-back">←</span>
                            <strong>+ Add Team</strong>
                          </div>

                          <div className="directory-demo-table">
                            <div className="directory-demo-table-head directory-demo-two-col">
                              <span>Team Name</span>
                              <span>Delete</span>
                            </div>

                            <div className="directory-demo-table-row directory-demo-two-col">
                              <span>Aging Ballers</span>
                              <span>🗑️</span>
                            </div>

                            <div className="directory-demo-table-row directory-demo-two-col">
                              <span>Youngz Baller</span>
                              <span>🗑️</span>
                            </div>

                            <div className="directory-demo-table-row directory-demo-two-col">
                              <span>New 2 Hoop</span>
                              <span>🗑️</span>
                            </div>
                          </div>

                          <div className="directory-demo-section-title-row">
                            <strong>Games</strong>
                            <strong>+ Add Game</strong>
                          </div>

                          <div className="directory-demo-game-row">
                            <span>02/05/26</span>
                            <span>Aging Ballers vs Youngz Baller</span>
                            <span>1:00 PM</span>
                          </div>
                        </div>

                        <div className="directory-demo-panel directory-demo-public-panel">
                          <div className="directory-demo-panel-header">Public View</div>

                          <div className="directory-demo-tabs">
                            <span className="active">Standings</span>
                            <span>Players</span>
                            <span>Schedule</span>
                          </div>

                          <div className="directory-demo-select">Adult Men 18+</div>

                          <div className="directory-demo-table">
                            <div className="directory-demo-table-head directory-demo-four-col">
                              <span>Position</span>
                              <span>Team</span>
                              <span>Wins</span>
                              <span>Losses</span>
                            </div>

                            <div className="directory-demo-table-row directory-demo-four-col">
                              <span>1</span>
                              <span>Aging Ballers</span>
                              <span>0</span>
                              <span>0</span>
                            </div>

                            <div className="directory-demo-table-row directory-demo-four-col">
                              <span>2</span>
                              <span>Youngz Baller</span>
                              <span>0</span>
                              <span>0</span>
                            </div>

                            <div className="directory-demo-table-row directory-demo-four-col">
                              <span>3</span>
                              <span>New 2 Hoop</span>
                              <span>0</span>
                              <span>0</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section> */}







              </section>
            )}




            {isResultsMode && (
              <DirectoryResults
                allSports={filters.sports}
                allAudiences={filters.audiences}
                allAges={filters.age_groups}
                allPrograms={filters.program_types}
              />
            )}
          </>
        )}
      </div>
      {!isResultsMode && <DirectoryFooter />}
    </div>
  );
}

export default Directory;